import { useEffect, useRef, useState } from 'react';
import { api, enc } from '../api';
import { fmtWhen } from '../format';
import { useApp, type AgentRow } from '../state';
import { AgentAvatar, AiTag, HumanAvatar } from './avatar';
import { Icon } from './icons';
import { AttachButton, AttachmentChips, PendingChips, filesFromDrop, filesFromPaste, useAttach, type Att } from './attach';

// Ask an agent. A drawer (right on desktop, bottom sheet on a phone) where a person talks to one agent about its
// work, optionally about one item. The agent answers in its own voice from the records; it cannot act from here.
// Opened from any "Ask <Agent>" button through openChat(); the latest conversation with that agent (about that item)
// continues unless the person starts a new one.
export type ChatTarget = { agent: string; item?: string | null; itemTitle?: string | null };
type Msg = { id: string; role: 'user' | 'agent'; body: string; cited_json?: string; cost_cents?: number; error?: string | null; created_at: string; attachments?: Att[] };
type Chat = { id: string; title: string; work_item_id: string | null; item_title?: string | null; message_count: number; cost_cents: number; updated_at: string };

export const openChat = (t: ChatTarget) => window.dispatchEvent(new CustomEvent('emm:chat', { detail: t }));

export function useChatDrawer() {
  const [target, setTarget] = useState<ChatTarget | null>(null);
  useEffect(() => {
    const on = (e: Event) => setTarget((e as CustomEvent<ChatTarget>).detail);
    window.addEventListener('emm:chat', on);
    return () => window.removeEventListener('emm:chat', on);
  }, []);
  return { target, close: () => setTarget(null) };
}

const STARTERS = (a: AgentRow, item?: string | null) => item
  ? ['Why did you draft it this way?', 'What would you change if I sent it back?', 'What did you not know when you wrote this?']
  : ['What are you working on right now?', 'What do you need from me?', `What did you finish this week?`];

const cited = (m: Msg): string[] => { try { const x = JSON.parse(m.cited_json || '[]'); return Array.isArray(x) ? x.map(String) : []; } catch { return []; } };

export function ChatDrawer({ target, onClose }: { target: ChatTarget; onClose: () => void }) {
  const { agentsByCode, me } = useApp();
  const a = agentsByCode.get(target.agent);
  const [chat, setChat] = useState<Chat | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [earlier, setEarlier] = useState<Chat[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const item = target.item ?? null;
  const attach = useAttach();

  // Resume the latest conversation with this agent about this item, if there is one.
  useEffect(() => {
    let live = true;
    setLoading(true); setChat(null); setMsgs([]); setErr('');
    api<{ chats: Chat[] }>('GET', `/agents/${enc(target.agent)}/chats${item ? `?item=${enc(item)}` : ''}`).then(async (r) => {
      if (!live) return;
      setEarlier(r.chats);
      const latest = r.chats[0];
      if (latest) { const d = await api<{ chat: Chat; messages: Msg[] }>('GET', `/chats/${enc(latest.id)}`); if (live) { setChat(d.chat); setMsgs(d.messages); } }
    }).catch((e) => live && setErr(e.message)).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [target.agent, item]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs, busy]);
  useEffect(() => { const on = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', on); return () => window.removeEventListener('keydown', on); }, [onClose]);
  useEffect(() => { if (!loading) inputRef.current?.focus(); }, [loading, chat?.id]);

  if (!a) return null;

  const ask = async (q?: string) => {
    const body = (q ?? text).trim();
    if ((!body && !attach.ids.length) || busy || attach.busy) return;
    const question = body || `(see the attached ${attach.ids.length === 1 ? 'file' : 'files'})`;
    const attachments = attach.ids;
    setBusy(true); setErr(''); setText('');
    const optimistic: Msg = { id: `tmp-${Date.now()}`, role: 'user', body: question, created_at: new Date().toISOString(), attachments: attach.items.filter((x) => x.att).map((x) => x.att!) };
    setMsgs((m) => [...m, optimistic]);
    try {
      if (!chat) {
        const d = await api<{ chat: Chat; messages: Msg[] }>('POST', `/agents/${enc(a.code)}/chats`, { work_item_id: item, body: question, attachments });
        setChat(d.chat); setMsgs(d.messages); setEarlier((e) => [d.chat, ...e]);
      } else {
        const d = await api<{ chat: Chat; messages: Msg[] }>('POST', `/chats/${enc(chat.id)}/messages`, { body: question, attachments });
        setChat(d.chat); setMsgs((m) => [...m.filter((x) => x.id !== optimistic.id), ...d.messages]);
      }
      attach.clear();
    } catch (e) { setErr((e as Error).message); setMsgs((m) => m.filter((x) => x.id !== optimistic.id)); setText(body); }
    finally { setBusy(false); }
  };
  const fresh = () => { setChat(null); setMsgs([]); setErr(''); inputRef.current?.focus(); };
  const resume = async (c: Chat) => { const d = await api<{ chat: Chat; messages: Msg[] }>('GET', `/chats/${enc(c.id)}`); setChat(d.chat); setMsgs(d.messages); };

  return (
    <div className="chat-scrim" onClick={onClose}>
      <aside className="chat" role="dialog" aria-label={`Ask ${a.name}`} onClick={(e) => e.stopPropagation()}>
        <header className="chat-head">
          <AgentAvatar agent={a} size="l" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 8 }}><b>{a.name}</b><AiTag /></div>
            <div className="sub">{a.role_title}{a.status && a.status !== 'idle' ? ` · ${a.status}` : ''}</div>
          </div>
          <button className="quiet sm" type="button" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </header>
        {(item || target.itemTitle) && <div className="chat-about"><span className="small muted">About</span> <a href={`#/work/${item}`}>{target.itemTitle ?? chat?.item_title ?? item}</a></div>}
        <div className="chat-body">
          {loading ? <p className="loading">Opening…</p> : (<>
            {!msgs.length && (
              <div className="chat-empty">
                <p>{a.voice ? `“${a.voice}”` : `Ask ${a.name} about its work.`}</p>
                <p className="small muted">{a.name} answers from the records only: its queue, its log, its drafts and the decisions on them. It cannot send, approve or move anything from here.</p>
                <div className="chat-starters">{STARTERS(a, item).map((s) => <button key={s} type="button" className="quiet sm" onClick={() => ask(s)}>{s}</button>)}</div>
                {earlier.length > 0 && !chat && <div className="small" style={{ marginTop: 14 }}><span className="muted">Earlier: </span>{earlier.slice(0, 3).map((c) => <button key={c.id} type="button" className="link" style={{ marginRight: 10 }} onClick={() => resume(c)}>{c.title.length > 40 ? c.title.slice(0, 40) + '…' : c.title}</button>)}</div>}
              </div>
            )}
            {msgs.map((m) => m.role === 'user' ? (
              <div key={m.id} className="chat-msg me"><div><div className="bubble">{m.body}</div><AttachmentChips list={m.attachments} size="s" /></div><HumanAvatar name={me.user.display_name} size="s" /></div>
            ) : (
              <div key={m.id} className={`chat-msg ${m.error ? 'err' : ''}`}><AgentAvatar agent={a} size="s" /><div>
                <div className="bubble">{m.body.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</div>
                <div className="meta small muted">{fmtWhen(m.created_at)}{m.cost_cents ? ` · $${(m.cost_cents / 100).toFixed(2)}` : ''}{cited(m).length > 0 && <> · {cited(m).slice(0, 4).map((id) => <a key={id} href={`#/work/${id}`} className="mono" style={{ marginLeft: 6 }}>{id.slice(-6)}</a>)}</>}</div>
              </div></div>
            ))}
            {busy && <div className="chat-msg"><AgentAvatar agent={a} size="s" /><div className="bubble thinking"><i /><i /><i /></div></div>}
            <div ref={endRef} />
          </>)}
        </div>
        <form className="chat-ask" onSubmit={(e) => { e.preventDefault(); ask(); }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); attach.add(filesFromDrop(e)); }}>
          {err && <div className="error" role="alert">{err}</div>}
          <PendingChips items={attach.items} onRemove={attach.remove} />
          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <AttachButton onFiles={attach.add} label="" />
            <textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder={`Ask ${a.name}… (paste a screenshot or attach a file)`} onPaste={(e) => { const f = filesFromPaste(e); if (f.length) { e.preventDefault(); attach.add(f); } }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }} disabled={busy} />
            <button type="submit" disabled={busy || attach.busy || (!text.trim() && !attach.ids.length)}>Ask</button>
          </div>
          <div className="small muted" style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', gap: 10 }}><span>Answers come from the records and what you attach. {a.name} cannot act from here.</span>{chat && <button type="button" className="link" onClick={fresh}>New conversation</button>}</div>
        </form>
      </aside>
    </div>
  );
}

/** The "Ask Marlowe" button, sized like its neighbours. */
export function AskButton({ agent, item, itemTitle, size = '', quiet = true }: { agent: AgentRow | undefined; item?: string | null; itemTitle?: string | null; size?: '' | 'sm'; quiet?: boolean }) {
  if (!agent) return null;
  return <button type="button" className={`${quiet ? 'quiet' : ''} ${size} ask`} onClick={() => openChat({ agent: agent.code, item, itemTitle })}><Icon name="chat" />Ask {agent.name}</button>;
}
